import { React, useMemo, useState } from 'react'
import {
  CheckCircleIcon,
  ClipboardIcon,
  CommandLineIcon,
  KeyIcon,
  PaperAirplaneIcon,
  SignalIcon,
} from '@heroicons/react/24/outline'

const baseUrl = 'https://api.whistlepulse.com/api'

const endpoints = [
  {
    id: 'single-message',
    label: 'Single Message',
    method: 'POST',
    path: '/messages/single',
    description: 'Send one SMS message to one recipient through the Whistle Pulse Messages API.',
    auth: 'API key headers',
    idempotent: true,
    body: {
      from: 'Whistle',
      to: '233501234567',
      message: 'Your Whistle Pulse verification code is 482190.',
    },
    response: {
      success: true,
      message: 'Message queued successfully',
      data: {
        message_id: 'msg_7L6e2NQG9',
        status: 'queued',
      },
    },
    notes: ['Protected by apiAuth.', 'Provide an Idempotency-Key header for safe retries.'],
  },
  {
    id: 'bulk-message',
    label: 'Bulk Message',
    method: 'POST',
    path: '/messages/bulk',
    description: 'Send the same SMS content to multiple recipients in one request.',
    auth: 'API key headers',
    idempotent: true,
    body: {
      from: 'Whistle',
      recipients: ['233501234567', '233241234567'],
      message: 'Your payment reminder is due today.',
    },
    response: {
      success: true,
      message: 'Bulk message queued successfully',
      data: {
        batch_id: 'batch_4Yp92',
        accepted: 2,
      },
    },
    notes: ['Protected by apiAuth.', 'The request body is validated by bulkMessageSchema.'],
  },
  {
    id: 'personalized-message',
    label: 'Personalized Message',
    method: 'POST',
    path: '/messages/personalized',
    description: 'Send recipient-specific SMS content in a single API call.',
    auth: 'API key headers',
    idempotent: true,
    body: {
      from: 'Whistle',
      messages: [
        {
          to: '233501234567',
          message: 'Hello Ama, your order WP-204 is ready.',
        },
        {
          to: '233241234567',
          message: 'Hello Kofi, your order WP-205 is ready.',
        },
      ],
    },
    response: {
      success: true,
      message: 'Personalized messages queued successfully',
      data: {
        batch_id: 'batch_5Kx18',
        accepted: 2,
      },
    },
    notes: ['Protected by apiAuth.', 'Each item can carry a unique recipient and message body.'],
  },
  {
    id: 'otp-send',
    label: 'Send OTP',
    method: 'POST',
    path: '/messages/otp/send',
    description: 'Generate and send a one-time password to a customer phone number.',
    auth: 'API key headers',
    idempotent: true,
    body: {
      to: '233501234567',
      channel: 'sms',
      length: 6,
      ttl: 300,
    },
    response: {
      success: true,
      message: 'OTP sent successfully',
      data: {
        otp_id: 'otp_R91xQ',
        expires_in: 300,
      },
    },
    notes: ['Protected by apiAuth.', 'The request body is validated by otpSendSchema.'],
  },
  {
    id: 'otp-verify',
    label: 'Verify OTP',
    method: 'POST',
    path: '/messages/otp/verify',
    description: 'Verify a submitted one-time password against the OTP session created by the API.',
    auth: 'API key headers',
    idempotent: true,
    body: {
      otp_id: 'otp_R91xQ',
      code: '482190',
    },
    response: {
      success: true,
      message: 'OTP verified successfully',
      data: {
        verified: true,
      },
    },
    notes: ['Protected by apiAuth.', 'The request body is validated by otpVerifySchema.'],
  },
  {
    id: 'callback',
    label: 'Delivery Callback',
    method: 'GET',
    path: '/messages/callback',
    description: 'Receive delivery updates from network providers through the callback controller.',
    auth: 'No API auth middleware on this route',
    idempotent: false,
    body: null,
    response: {
      success: true,
      message: 'Callback received',
    },
    notes: ['This route is public in the router you provided.', 'Use it for delivery receipts or provider status callbacks.'],
  },
]

const languages = ['JavaScript', 'cURL', 'Python']

function getHeaderRequirements(endpoint) {
  if (endpoint.auth !== 'API key headers') {
    return [
      {
        name: 'Accept',
        value: 'application/json',
        requirement: 'Recommended',
        description: 'Tells the API you expect JSON in the response.',
      },
    ]
  }

  return [
    {
      name: 'x-account-id',
      value: 'YOUR_ACCOUNT_ID',
      requirement: 'Required',
      description: 'Identifies the Whistle Pulse account making the API request.',
    },
    {
      name: 'x-api-key',
      value: 'YOUR_API_KEY',
      requirement: 'Required',
      description: 'Authenticates the request with your Whistle Pulse API key.',
    },
    {
      name: 'Content-Type',
      value: 'application/json',
      requirement: 'Required',
      description: 'Required when sending a JSON request body.',
    },
    {
      name: 'Idempotency-Key',
      value: 'unique-request-key-001',
      requirement: endpoint.idempotent ? 'Required' : 'Optional',
      description: 'Use one unique value per new request. Reuse it only when retrying the same request.',
    },
    {
      name: 'Accept',
      value: 'application/json',
      requirement: 'Recommended',
      description: 'Tells the API you expect JSON in the response.',
    },
  ]
}

function buildSnippet(endpoint, language) {
  const url = `${baseUrl}${endpoint.path}`
  const payload = endpoint.body ? JSON.stringify(endpoint.body, null, 2) : null
  const authHeaders = endpoint.auth === 'API key headers'

  if (language === 'cURL') {
    return endpoint.body
      ? `curl -X ${endpoint.method} "${url}" \\
${authHeaders ? '  -H "x-account-id: YOUR_ACCOUNT_ID" \\\n  -H "x-api-key: YOUR_API_KEY" \\\n' : ''}\
  -H "Content-Type: application/json" \\${endpoint.idempotent ? '\n  -H "Idempotency-Key: unique-request-key-001" \\' : ''}
  -d '${payload}'`
      : `curl -X ${endpoint.method} "${url}" \\
${authHeaders ? '  -H "x-account-id: YOUR_ACCOUNT_ID" \\\n  -H "x-api-key: YOUR_API_KEY"' : '  -H "Accept: application/json"'}`
  }

  if (language === 'Python') {
    return `import requests

url = "${url}"
headers = {
${authHeaders ? '    "x-account-id": "YOUR_ACCOUNT_ID",\n    "x-api-key": "YOUR_API_KEY",\n' : ''}\
    "Content-Type": "application/json",
${endpoint.idempotent ? '    "Idempotency-Key": "unique-request-key-001",\n' : ''}
}
${endpoint.body ? `payload = ${payload}\n\nresponse = requests.${endpoint.method.toLowerCase()}(url, headers=headers, json=payload)` : `response = requests.${endpoint.method.toLowerCase()}(url, headers=headers)`}
response.raise_for_status()
print(response.json())`
  }

  return `const url = "${url}";

const response = await fetch(url, {
  method: "${endpoint.method}",
  headers: {
${authHeaders ? '    "x-account-id": "YOUR_ACCOUNT_ID",\n    "x-api-key": "YOUR_API_KEY",\n' : ''}\
    "Content-Type": "application/json"${endpoint.idempotent ? ',\n    "Idempotency-Key": "unique-request-key-001"' : ''}
  }${endpoint.body ? `,
  body: JSON.stringify(${payload})` : ''}
});

if (!response.ok) {
  throw new Error(\`Whistle Pulse API error: \${response.status}\`);
}

const data = await response.json();
console.log(data);`
}

const Developers = () => {
  const [activeEndpointId, setActiveEndpointId] = useState(endpoints[0].id)
  const [activeLanguage, setActiveLanguage] = useState(languages[0])
  const [copied, setCopied] = useState(false)

  const activeEndpoint = endpoints.find((endpoint) => endpoint.id === activeEndpointId) || endpoints[0]
  const snippet = useMemo(() => buildSnippet(activeEndpoint, activeLanguage), [activeEndpoint, activeLanguage])
  const headerRequirements = useMemo(() => getHeaderRequirements(activeEndpoint), [activeEndpoint])

  const copySnippet = async () => {
    if (!navigator.clipboard) return

    await navigator.clipboard.writeText(snippet)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <section id="developers" className="bg-[#071d33]">
      <div className="max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8 lg:py-20 mx-auto">
        <div className="grid min-w-0 gap-10 lg:grid-cols-[0.95fr_1.35fr] lg:items-start">
          <div className="min-w-0 text-white">
            <p className="text-blue-300 text-sm font-semibold">Developer API integration</p>
            <h2 className="mt-4 text-left font-bold text-3xl sm:text-4xl sm:leading-10">
              Build SMS and OTP workflows in minutes
            </h2>
            <p className="mt-5 text-blue-50 leading-7">
              Use Whistle Pulse APIs to send single, bulk, and personalized messages, handle provider callbacks, and
              add OTP verification to your product.
            </p>

            <div className="mt-8 grid gap-3">
              {[
                ['Base URL', baseUrl, CommandLineIcon],
                ['Auth Headers', 'x-account-id + x-api-key', KeyIcon],
                ['Response Format', 'application/json', SignalIcon],
              ].map(([title, value, Icon]) => (
                <div
                  key={title}
                  className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/5 p-4"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-500/15">
                    <Icon className="h-5 w-5 text-blue-300" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-blue-100">{title}</p>
                    <p className="mt-1 min-w-0 break-words font-mono text-sm font-semibold leading-5 text-white">
                      {value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 space-y-3">
              {endpoints.map((endpoint) => (
                <button
                  key={endpoint.id}
                  type="button"
                  onClick={() => setActiveEndpointId(endpoint.id)}
                  className={`w-full rounded-lg border p-4 text-left transition ${
                    activeEndpoint.id === endpoint.id
                      ? 'border-blue-400 bg-blue-500/15'
                      : 'border-white/10 bg-white/5 hover:border-blue-300/60'
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="rounded bg-white/10 px-2 py-1 text-xs font-bold text-blue-200">
                      {endpoint.method}
                    </span>
                    <span className="min-w-0 font-semibold text-white">{endpoint.label}</span>
                  </span>
                  <span className="mt-2 block text-sm text-blue-100">{endpoint.description}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="min-w-0 overflow-hidden rounded-lg border border-white/10 bg-[#0c2e4e] text-white shadow-2xl shadow-black/20">
            <div className="border-b border-white/10 p-4 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-blue-500 px-2 py-1 text-xs font-bold">{activeEndpoint.method}</span>
                    <span className="min-w-0 break-all font-mono text-sm text-blue-100">{activeEndpoint.path}</span>
                  </div>
                  <h3 className="mt-3 text-xl font-bold">{activeEndpoint.label}</h3>
                  <p className="mt-2 text-sm leading-6 text-blue-100">{activeEndpoint.description}</p>
                </div>
                <a
                  href="https://dashboard.whistlepulse.com/auth/register"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-[#071d33] hover:bg-blue-50"
                >
                  <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />
                  Get API keys
                </a>
              </div>
            </div>

            <div className="grid min-w-0 gap-0 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="min-w-0 border-b border-white/10 p-4 sm:p-6 lg:border-b-0 lg:border-r">
                <div>
                  <p className="text-sm font-semibold text-blue-200">Required Headers</p>
                  <div className="mt-2 overflow-hidden rounded bg-black/20">
                    {headerRequirements.map((header) => (
                      <div key={header.name} className="border-b border-white/10 p-3 last:border-b-0">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-mono text-sm font-semibold text-white">{header.name}</span>
                          <span
                            className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
                              header.requirement === 'Required'
                                ? 'bg-blue-400 text-[#071d33]'
                                : 'bg-white/10 text-blue-100'
                            }`}
                          >
                            {header.requirement}
                          </span>
                        </div>
                        <p className="mt-2 break-words font-mono text-xs leading-5 text-blue-100">{header.value}</p>
                        <p className="mt-1 text-xs leading-5 text-blue-200">{header.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <p className="text-sm font-semibold text-blue-200">Request Body</p>
                  <pre className="mt-2 max-h-56 max-w-full overflow-auto rounded bg-black/20 p-3 text-xs leading-6 text-blue-50">
                    {activeEndpoint.body ? JSON.stringify(activeEndpoint.body, null, 2) : 'No request body required.'}
                  </pre>
                </div>

                <div className="mt-6">
                  <p className="text-sm font-semibold text-blue-200">Example Response</p>
                  <pre className="mt-2 max-h-56 max-w-full overflow-auto rounded bg-black/20 p-3 text-xs leading-6 text-blue-50">
                    {JSON.stringify(activeEndpoint.response, null, 2)}
                  </pre>
                </div>

                <div className="mt-6 space-y-2">
                  {activeEndpoint.notes.map((note) => (
                    <p key={note} className="flex gap-2 text-sm text-blue-100">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-300" aria-hidden="true" />
                      {note}
                    </p>
                  ))}
                </div>
              </div>

              <div className="min-w-0 p-4 sm:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="grid grid-cols-3 rounded-md bg-black/20 p-1 sm:flex">
                    {languages.map((language) => (
                      <button
                        key={language}
                        type="button"
                        onClick={() => setActiveLanguage(language)}
                        className={`rounded px-2 py-1.5 text-xs font-semibold sm:px-3 sm:text-sm ${
                          activeLanguage === language ? 'bg-blue-500 text-white' : 'text-blue-100 hover:text-white'
                        }`}
                      >
                        {language}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={copySnippet}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm font-semibold text-blue-50 hover:bg-white/10"
                  >
                    <ClipboardIcon className="h-5 w-5" aria-hidden="true" />
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>

                <pre className="mt-4 h-96 max-w-full whitespace-pre-wrap break-words rounded-md bg-[#071d33] p-3 text-xs leading-6 text-blue-50 sm:h-[520px] sm:overflow-auto sm:whitespace-pre sm:p-4 sm:text-sm">
                  <code>{snippet}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 grid items-center gap-6 border-t border-white/10 pt-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-4">
            <p className="text-5xl font-bold leading-10 text-blue-400">300M+</p>
            <p className="mt-3 text-gray-300">Messages delivered annually</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3 lg:col-span-8">
            <div>
              <p className="text-3xl font-semibold text-blue-400">99.99%</p>
              <p className="mt-1 text-gray-300">Message delivery rate</p>
            </div>
            <div>
              <p className="text-3xl font-semibold text-blue-400">8K+</p>
              <p className="mt-1 text-gray-300">API integrations</p>
            </div>
            <div>
              <p className="text-3xl font-semibold text-blue-400">45K+</p>
              <p className="mt-1 text-gray-300">Businesses connected</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Developers
